import { useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { Paginacao } from "@/components/Paginacao";
import { useDebounce, usePaginado } from "@/hooks/usePaginado";
import { RoleGuard } from "@/components/RoleGuard";
import { PageHeader } from "@/components/padroes";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import type { Tomador } from "@/data/mock";
import type { Headcount } from "@/data/dashboard";
import { toast } from "sonner";
import { Plus, Trash2, Building2, Search, Loader2 } from "lucide-react";

function formatCNPJ(raw: string) {
  const d = raw.replace(/\D/g, "").slice(0, 14);
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
  if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

const RHTomadores = () => {
  const [busca, setBusca] = useState("");
  const buscaAtrasada = useDebounce(busca.trim());
  const {
    items: tomadores,
    total,
    page,
    pageSize,
    carregando,
    setPage,
    recarregar: load,
  } = usePaginado<Tomador>("/tomadores", { q: buscaAtrasada });
  const [razaoSocial, setRazaoSocial] = useState("");
  const [cnpj, setCnpj] = useState("");
  const [saving, setSaving] = useState(false);
  /** Pessoas ativas por tomador (id → quantidade), do Headcount. null = indisponível. */
  const [pessoas, setPessoas] = useState<Map<string, number> | null>(null);

  useEffect(() => {
    api
      .get<Headcount>("/dashboard/headcount")
      .then((h) => setPessoas(new Map(h.porTomador.map((g) => [g.chave, g.total]))))
      .catch(() => setPessoas(null));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const digits = cnpj.replace(/\D/g, "");
    if (!razaoSocial.trim()) { toast.error("Informe a razão social"); return; }
    if (digits.length !== 14) { toast.error("CNPJ deve ter 14 dígitos"); return; }
    setSaving(true);
    try {
      await api.post("/tomadores", { razaoSocial: razaoSocial.trim(), cnpj: digits });
      toast.success("Tomador cadastrado");
      setRazaoSocial("");
      setCnpj("");
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao cadastrar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/tomadores/${id}`);
      toast.success("Tomador removido");
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao remover");
    }
  };

  return (
    <RoleGuard role="rh">
      <AppLayout title="Tomadores">
        <div className="mx-auto flex max-w-7xl flex-col gap-4">
          <PageHeader titulo="Tomadores" subtitulo="Empresas contratantes que recebem as NFs dos PJ." />

          <div className="flex flex-wrap items-start gap-4">
            {/* Formulário */}
            <section className="flex flex-[1_1_300px] flex-col gap-3.5 rounded-xl border bg-card px-5 py-[18px]">
              <h3 className="flex items-center gap-2 text-[15px] font-semibold">
                <Plus className="h-4 w-4 text-primary" /> Novo tomador
              </h3>
              <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="razao">Razão social *</Label>
                  <Input
                    id="razao"
                    value={razaoSocial}
                    onChange={(e) => setRazaoSocial(e.target.value)}
                    placeholder="Ex.: Empresa Contratante S.A."
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="cnpj">CNPJ *</Label>
                  <Input
                    id="cnpj"
                    value={cnpj}
                    onChange={(e) => setCnpj(formatCNPJ(e.target.value))}
                    placeholder="00.000.000/0000-00"
                    maxLength={18}
                  />
                </div>
                <Button type="submit" disabled={saving} className="self-start">
                  {saving && <Loader2 className="animate-spin" />}
                  Adicionar
                </Button>
              </form>
            </section>

            {/* Lista */}
            <div className="flex min-w-0 flex-[2_1_520px] flex-col gap-3">
              <section className="overflow-hidden rounded-xl border bg-card">
                <div className="border-b border-border-soft p-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={busca}
                      onChange={(e) => setBusca(e.target.value)}
                      placeholder="Buscar por razão social ou CNPJ"
                      className="h-9 pl-9"
                      aria-label="Buscar tomadores"
                    />
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Razão social</TableHead>
                      <TableHead>CNPJ</TableHead>
                      {pessoas && <TableHead className="text-right">Pessoas</TableHead>}
                      <TableHead className="w-12" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tomadores.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="py-12 text-center text-muted-foreground">
                          <Building2 className="mx-auto mb-2 h-8 w-8 opacity-30" />
                          {carregando
                            ? "Carregando..."
                            : buscaAtrasada
                              ? "Nenhum tomador encontrado"
                              : "Nenhum tomador cadastrado"}
                        </TableCell>
                      </TableRow>
                    ) : (
                      tomadores.map((t) => (
                        <TableRow key={t.id}>
                          <TableCell className="font-medium">{t.razaoSocial}</TableCell>
                          <TableCell className="whitespace-nowrap text-muted-foreground">{formatCNPJ(t.cnpj)}</TableCell>
                          {pessoas && <TableCell className="text-right">{pessoas.get(t.id) ?? 0}</TableCell>}
                          <TableCell className="px-2 text-center">
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                  aria-label={`Remover ${t.razaoSocial}`}
                                >
                                  <Trash2 className="h-[15px] w-[15px]" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Remover tomador?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Todos os usuários vinculados a <strong>{t.razaoSocial}</strong> serão desvinculados. Esta ação não pode ser desfeita.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    onClick={() => handleDelete(t.id)}
                                  >
                                    Remover
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </section>
              <Paginacao
                page={page}
                pageSize={pageSize}
                total={total}
                onPage={setPage}
                carregando={carregando}
                rotulo="tomadores"
              />
            </div>
          </div>
        </div>
      </AppLayout>
    </RoleGuard>
  );
};

export default RHTomadores;
